// Page d'un chantier (lecture) : origine, responsable, dates, consignes d'accès, temps prévu, effectif retenu, postes et étapes ;
// onglets Planning (incrément 3) et Avancement (incrément 4 : trois mesures séparées, jours travaillés vers la page du jour) ;
// onglets Réserves et Pièces posées (incrément 5 : ouvrir, lever, poser — la pose crée ou complète la fiche de l'équipement) ;
// onglet Matériel (15/10/2026) : matériel à commander par fournisseur, écart avec les pièces posées, export CSV et PDF.
// Le chargé d'affaires y trouve « Continuer la préparation » (ou « Modifier la structure » une fois lancé). Aucun montant.
// Réception des travaux (15/10/2026) : saisie et correction motivée dans la fiche « Chantier » (point de départ de la conservation).
import { Link, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Camera, FlaskConical, MapPin, Pencil, Wrench } from 'lucide-react';
import { api } from '../../../lib/api.ts';
import { date, dateHeure, pluriel } from '../../../lib/format.ts';
import { Badge, Chargement, EntetePage, EtatErreur, FilContexte, LienBouton, Onglets, Panneau, Valeur } from '../../../components/ui.tsx';
import { BadgeStatutChantier } from '../../../components/statuts.tsx';
import { BadgesRevision, BandeauRevision, heures, quantite, ResumeEffectif, ResumeTemps, TempsEtape } from './commun.tsx';
import { PanneauPlanning } from './Planning.tsx';
import { PanneauAvancement } from './Avancement.tsx';
import { PanneauPieces, PanneauReserves } from './Terrain.tsx';
import { PanneauMateriel } from './Materiel.tsx';
import type { CadreSaisie, VueChantier } from './types.ts';
import { libelleChantier } from './saisie.ts';
import { FicheReception } from './Reception.tsx';

type Onglet = 'structure' | 'planning' | 'avancement' | 'reserves' | 'pieces' | 'materiel';
const ONGLETS: Onglet[] = ['structure', 'planning', 'avancement', 'reserves', 'pieces', 'materiel'];

export function PageChantier() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const brut = params.get('onglet');
  const onglet: Onglet = ONGLETS.includes(brut as Onglet) ? brut as Onglet : 'structure';
  const q = useQuery({ queryKey: ['chantier', id], queryFn: () => api.get<VueChantier>(`/api/chantiers/${id}`) });
  // Volet ouvert depuis l'onglet Réserves ou Pièces posées : ?volet=reserve|piece (pour y revenir directement).
  const volet = params.get('volet');
  const changerVolet = (v: string | null) => { const n = new URLSearchParams(params); if (v) n.set('volet', v); else n.delete('volet'); setParams(n, { replace: !v }); };
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const v = q.data!;
  const c = v.chantier;
  const cadre: CadreSaisie = {
    chantierId: c.id, posteId: null, libelle: libelleChantier(c),
    postes: v.postes.filter((p) => !p.retire).map((p) => ({ id: p.id, label: p.label, lieu_id: p.location?.id ?? null, a_poser: p.a_poser,
      etapes: p.etapes.filter((e) => !e.retiree).map((e) => ({ id: e.id, label: e.label, photo_required: e.photo_required })) })),
  };
  const enPreparation = c.status === 'preparation';
  const action = v.droits.preparer && ['preparation', 'lance', 'suspendu'].includes(c.status)
    ? <LienBouton variante="principal" icone={<Pencil aria-hidden />} to={`/interventions/chantiers/${c.id}/preparer`}>{enPreparation ? 'Continuer la préparation' : 'Modifier la structure'}</LienBouton>
    : null;
  return (
    <div className="pile-l">
      <EntetePage
        fil={<FilContexte noeuds={[{ type: '', libelle: 'Chantiers', to: '/interventions/chantiers' }, { type: '', libelle: c.reference ?? 'En préparation' }]} />}
        titre={<>{c.reference && <span className="mono">{c.reference} </span>}{c.title}</>}
        sousTitre={<span className="rangee" style={{ gap: 8 }}><BadgeStatutChantier statut={c.status} />{c.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}
          {c.lance_le && <span className="petit">Lancé le {dateHeure(c.lance_le)}{c.lance_par ? ` par ${c.lance_par}` : ''}</span>}</span>}
        actions={action} />
      <BandeauRevision v={v} />
      <Onglets libelle="Sections du chantier" actif={onglet} changer={(o) => { const n = new URLSearchParams(params); n.delete('volet'); if (o !== 'structure') n.set('onglet', o); else n.delete('onglet'); setParams(n, { replace: true }); }}
        onglets={[{ id: 'structure', libelle: 'Postes et étapes' }, { id: 'planning', libelle: 'Planning' }, { id: 'avancement', libelle: 'Avancement' },
          { id: 'reserves', libelle: 'Réserves' }, { id: 'pieces', libelle: 'Pièces posées' }, { id: 'materiel', libelle: 'Matériel' }]} />
      {onglet === 'planning' ? <PanneauPlanning chantierId={c.id} /> : onglet === 'avancement' ? <PanneauAvancement chantierId={c.id} />
        : onglet === 'materiel' ? <PanneauMateriel chantierId={c.id} />
        : onglet === 'reserves' ? <PanneauReserves cadre={cadre} voletOuvert={volet === 'reserve'} ouvrir={() => changerVolet('reserve')} fermer={() => changerVolet(null)} />
          : onglet === 'pieces' ? <PanneauPieces cadre={cadre} voletOuvert={volet === 'piece'} ouvrir={() => changerVolet('piece')} fermer={() => changerVolet(null)} /> : <>

      <div className="grille-2 ch-grille">
        <Panneau titre="Chantier">
          <dl className="fiche ch-fiche">
            <div><dt>Site</dt><dd>{c.site ? <Link to={`/sites/${c.site.id}`}>{c.site.nom}</Link> : <Valeur />}{c.site?.adresse ? <span className="petit discret"> — {c.site.adresse}{c.site.ville ? `, ${c.site.ville}` : ''}</span> : null}</dd></div>
            <div><dt>Origine</dt><dd>{c.devis
              ? <>Devis <Link className="mono" to={`/etudes/${c.devis.dossier.id}/devis/${c.devis.id}?revision=${c.devis.revision_id}`}>{c.devis.number}</Link>, révision {c.devis.rev_no}{c.devis.variante !== 'Base' ? ` (${c.devis.variante})` : ''}{c.devis.acceptee_le ? `, acceptée le ${date(c.devis.acceptee_le)}` : ''}</>
              : 'Sans devis'}{v.dernier_report && <span className="petit secondaire" style={{ display: 'block' }}>Mis à jour depuis la révision {v.dernier_report.rev_no} le {dateHeure(v.dernier_report.le)}{v.dernier_report.par ? ` par ${v.dernier_report.par}` : ''}</span>}</dd></div>
            <div><dt>Responsable</dt><dd><Valeur v={c.responsable?.nom} inconnu="Non désigné" /></dd></div>
            <div><dt>Dates prévues</dt><dd>{c.planned_start || c.planned_end ? `${c.planned_start ? `du ${date(c.planned_start)}` : ''}${c.planned_end ? ` au ${date(c.planned_end)}` : ''}` : <Valeur inconnu="Non fixées" />}</dd></div>
            <div className="ch-fiche-large"><dt>Consignes d’accès</dt><dd style={{ whiteSpace: 'pre-wrap' }}><Valeur v={c.site_notes} inconnu="Aucune" /></dd></div>
            {!enPreparation && <FicheReception v={v} />}
          </dl>
        </Panneau>
        <Panneau titre="Temps et effectif">
          <div className="pile">
            <ResumeTemps v={v} />
            <ResumeEffectif e={v.effectif} />
          </div>
        </Panneau>
      </div>

      <Panneau serre titre={`${pluriel(v.postes.filter((p) => !p.retire).length, 'poste')} — ${pluriel(v.temps.nb_etapes, 'étape')}`}>
        <ol className="ch-postes-lecture">
          {v.postes.map((p) => (
            <li key={p.id} className={p.retire ? 'ch-retire' : undefined}>
              <details open={v.postes.length <= 3}>
                <summary>
                  <span className="ch-poste-titre">{p.label}</span>
                  <span className="petit discret chiffre">{heures(p.temps.heures)}{p.temps.a_renseigner ? ` + ${p.temps.a_renseigner} à renseigner` : ''} — {pluriel(p.temps.nb_etapes, 'étape')}</span>
                  <span className="rangee" style={{ gap: 6 }}>
                    {p.place === 'bureau' && <Badge ton="info" icone={false}>Au bureau</Badge>}
                    {p.location && <Badge icone={<MapPin size={14} aria-hidden />}>{p.location.label}</Badge>}
                    {p.equipment && <Badge icone={<Wrench size={14} aria-hidden />}>{p.equipment.label}</Badge>}
                    {p.retire && <Badge ton="neutre">Retiré</Badge>}
                  </span>
                </summary>
                <ol className="ch-etapes-lecture">
                  {p.etapes.map((e) => (
                    <li key={e.id} className={e.retiree ? 'ch-retire' : undefined}>
                      <span>{e.label}{e.photo_required && <Camera size={14} aria-label="Photo demandée" className="discret" style={{ marginLeft: 6 }} />}</span>
                      <TempsEtape e={e} />
                      {e.origin === 'hors_devis' && <Badge icone={false}>Hors devis</Badge>}
                      <BadgesRevision ajoutee={e.ajoutee_par_revision} retireeDuDevis={e.retiree_du_devis?.rev_no} faite={e.retiree_du_devis?.faite} />
                      {e.retiree && !e.retiree_du_devis && <span className="petit discret">Retirée : {e.retiree.motif}</span>}
                    </li>
                  ))}
                </ol>
                {p.a_poser.length > 0 && (
                  <p className="petit ch-a-poser"><strong>À poser :</strong> {p.a_poser.map((a) => `${a.label}${a.reference ? ` (${a.reference})` : ''} × ${quantite(a.qty, a.unit)}`).join(' ; ')}</p>
                )}
              </details>
            </li>
          ))}
        </ol>
      </Panneau>
      </>}
    </div>
  );
}
